import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { Button, Divider, Form, Input, Modal, notification, Spin, Typography } from 'antd'
import { useHistory } from 'react-router-dom'
import qs from 'qs'
import axios from 'axios'
import omit from 'lodash/omit'
import * as Sentry from '@sentry/react'
import { usePostHog } from 'posthog-js/react'
import { useManualQuery } from 'graphql-hooks'

import { actions } from '../create-company-page/actions'
import { getCompanyAndUserInfo } from '../../../graphql/custom-queries'

import { useAppSelector } from '../../../store/hooks'
import { setUserId } from '../../../store/user-id-slice'
import { setAuthCompany } from '../../../store/auth-company-slice'
import { setAuthState, selectAuthStateSlice } from '../../../store/auth-state-slice'
import { setAuthUser, selectAuthUserSlice } from '../../../store/auth-user-slice'
import { setLocale } from '../../../store/locale-slice'
import { AppDispatch } from '../../../store/configure'

import { IAuthNextStep, confirmSigninWithCustomChallengeAnswer } from '@vacationtracker/shared/services/auth'
import { SLACK_REQUIRED_BOT_SCOPES } from '@vacationtracker/shared/data/slack'
import { SLACK_REQUIRED_USER_SCOPES } from '@vacationtracker/shared/data/slack'
import { getUserId as getMsUserId } from '../../../services/api/microsoft'
import { getUserId as getGoogleUserId } from '../../../services/api/google'
import { track } from '../../../services/analytics/analytics'
import { updateSlackToken } from '../../../services/api/slack'
import { MicrosoftAuth } from '../../../services/auth/microsoft/microsoft'
import { SlackAuth } from '../../../services/auth/slack/slack'
import { GoogleAuth } from '../../../services/auth/google/google'
import { logout } from '../../../services/auth/logout-handler'
import { signin } from '@vacationtracker/shared/services/auth'

import { wait } from '@vacationtracker/shared/functions/wait'
import IntlMessages from '@vacationtracker/shared/components/utils/IntlMessages'
import { availableLanguages } from '@vacationtracker/shared/i18n'
import { getCookieByName, parseAndPrepareUtmCookie } from '../../../util/get-and-parse-cookie'
import { setCrispSessionInfo } from '../../../util/set-crisp-session-info'
import { openSupportChat } from '../../../util/open-support-chat'

import CompanyAlreadySignedUpModal from '../../../components/company-already-signed-up-modal'
import CompanyExistsModal from '../../../components/modal-company-exists'
import OtherPlatformLoginModal from '../../../components/other-platform-login-modal'
import SignupWithAnotherPlatformModal from '../../../components/signup-with-another-platform-modal'

import { Platform, PlatformEnum } from '@vacationtracker/shared/data/platforms'
import { ICheckUserId, SubscriptionPlanEnum } from '@vacationtracker/shared/types/company'
import { AuthStateEnum } from '@vacationtracker/shared/types/auth-state'
import { IContactAdminData } from '../../../types/auth'
import { ITokens, IMicrosoftUser, TenantId } from '../../../types/microsoft'
import { IGetCompanyAndUserInfo } from '../../../types/custom-queries'
import { FrontendUrls } from '../../../types/urls'
import { CrispSegmentsEnum } from '../../../types/crisp'
import { EmailNotValid, EmailPlatformUserInactive, UserExistsException, UserNotFound } from '@vacationtracker/shared/errors/users'
import { SlackIcon, TeamsIcon, GoogleIcon } from '../../../components/icons'


const { Link, Paragraph, Title, Text } = Typography


interface IConnectPlatformProps {
  focusedPlatform: PlatformEnum | null
  goToLogin: (state?: string) => void
  dispatch: Function
  reduxDispatch: AppDispatch
  msAuth: MicrosoftAuth
  slackAuth: SlackAuth
  googleAuth: GoogleAuth
}

const ConnectPlatform = ({
  focusedPlatform,
  goToLogin,
  dispatch,
  reduxDispatch,
  msAuth,
  slackAuth,
  googleAuth,
}: IConnectPlatformProps): React.ReactElement => {
  const { formatMessage } = useIntl()
  const posthog = usePostHog()
  const [form] = Form.useForm()
  const history = useHistory()

  const { authUser } = useAppSelector(selectAuthUserSlice)
  const { authState } = useAppSelector(selectAuthStateSlice)

  const [redirectUrl, setRedirectUrl] = useState(FrontendUrls.dashboard)
  const [signinInProgress, setSigninInProgress] = useState(false)
  const [userEmail, setUserEmail] = useState('')
  const [selectedPlatform, setSelectedPlatform] = useState<PlatformEnum | null>(null)
  const [contactAdminModal, setContactAdminModal] = useState<IContactAdminData | null>(null)
  const [companyExists, setCompanyExists] = useState(false)
  const [showSlackLoginRequiredModal, setShowSlackLoginRequiredModal] = useState(false)
  const [userWorkspaceName, setUserWorkspaceName] = useState('')
  const [existsOnAnotherPlatform, setExistsOnAnotherPlatform] = useState('')
  const [existingOrganizationName, setExistingOrganizationName] = useState('')
  const [companyAlreadySignedUpShowModal, setCompanyAlreadySignedUpShowModal] = useState(false)
  const [otherPlatformLogin, setOtherPlatformLogin] = useState(false)
  const [signupWithAnotherPlatformShowModal, setSignupWithAnotherPlatformShowModal] = useState(false)
  const [domainPlatform, setDomainPlatform] = useState<string | null>(null)
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlanEnum>(SubscriptionPlanEnum.complete)

  const [getCompanyAndUserInfoQuery] = useManualQuery<IGetCompanyAndUserInfo, { userId: string }>(getCompanyAndUserInfo)

  const queryParams = qs.parse(location.search, { ignoreQueryPrefix: true })

  useEffect(() => {
    if (queryParams?.redirect) {
      setRedirectUrl(queryParams.redirect as FrontendUrls)
    }

    if (queryParams?.subscriptionPlan) {
      const preselectedSubscriptionPlan = queryParams?.subscriptionPlan === 'Free' ? SubscriptionPlanEnum.freemium : queryParams?.subscriptionPlan as SubscriptionPlanEnum
      localStorage.setItem('vtSubscriptionPlan', preselectedSubscriptionPlan)
      setSelectedPlan(preselectedSubscriptionPlan)
    }
    if (queryParams?.affiliateCode) {
      localStorage.setItem('vtAffiliateCode', queryParams.affiliateCode as string)
    }

    if (authUser.id && authState === AuthStateEnum.signedIn) {
      history.push(redirectUrl)
    }
  }, [authState, authUser])

  useEffect(() => {
    reduxDispatch(setAuthState(AuthStateEnum.createCompany))
    track('SIGNUP_PAGE_VIEWED', {})

    setCrispSessionInfo(
      [CrispSegmentsEnum.chat, CrispSegmentsEnum.support, CrispSegmentsEnum.dashboard, CrispSegmentsEnum.onSignupPage],
      { signupStep: 'signup-screen'}
    )
    const emailFromSessionStorage = sessionStorage.getItem('vtUserEmail')
    if (emailFromSessionStorage) {
      form.setFieldsValue({ email: emailFromSessionStorage })
      setUserEmail(emailFromSessionStorage)
    }

    // Handle redirects
    if (queryParams?.redirect || queryParams?.forgotpassword) {
      history.push(FrontendUrls.signin + location.search)
      return
    }
  }, [])

  useEffect(() => {
    if (userEmail) {
      sessionStorage.setItem('vtUserEmail', userEmail)
    }
  }, [userEmail])

  let numberOfRetry = 0
  const getCompanyAndUser = async (id: string) => {
    try {
      const response = await getCompanyAndUserInfoQuery({ variables: { userId: id }})
      if (!response.data || response.error) throw response.error
      if (response.data.getCompany && response.data.getUser && response.data.getUser.name) {
        reduxDispatch(setAuthCompany(response.data.getCompany))
        reduxDispatch(setAuthUser(response.data.getUser))
        if (response.data.getUser.locale) {
          reduxDispatch(setLocale(availableLanguages[response.data.getUser.locale]))
        }
        Sentry.setUser({
          id: response.data.getUser.id,
          companyId: response.data.getCompany.id,
        })
      } else {
        throw new Error('No current user, retry')
      }
    } catch (error) {
      if (numberOfRetry >= 10) {
        // Log only before logout
        console.debug('ERROR GET COMPANY AND USER', error)
        logout({
          history,
          reduxDispatch: reduxDispatch,
          userId: id,
        })
      } else if (![FrontendUrls.signin, FrontendUrls.signup].includes(location.pathname as FrontendUrls)) {
        numberOfRetry++
        await wait(200 * numberOfRetry)
        return await getCompanyAndUser(id)
      }
    }
  }

  const getUsersByEmail = async (email: string) => {
    const res = await axios.post(`${process.env.REACT_APP_API_URL}/core/auth-info`, {
      email,
    })
    const platform: Platform = res.data.platform
    setSelectedPlatform(PlatformEnum.Email)
    if (platform !== 'email') {
      setExistsOnAnotherPlatform(platform)
      setCompanyAlreadySignedUpShowModal(true)
      return
    } else if (platform === 'email') {
      throw new Error('ALREADY_SIGNED_UP')
    }


    localStorage.setItem('vtCreateUser', JSON.stringify({
      platform: 'email',
      mail: email,
      userEmail: email,
    }))
    setUserEmail(email)
  }

  const getSlackTokens = () => {
    slackAuth.signin(
      SLACK_REQUIRED_BOT_SCOPES,
      SLACK_REQUIRED_USER_SCOPES
    ).then(async (response) => {
      setUserWorkspaceName(response.team.name)
      slackAuth.setBotToken(response.access_token)
      slackAuth.setUserToken(response.authed_user.access_token)
      await updateSlackToken(response.authed_user.access_token, response.access_token)
      setShowSlackLoginRequiredModal(false)
      dispatch(setUserId(localStorage.getItem('userId') as string))
    }).catch(error => {
      notification.error({
        message: formatMessage({ id: 'error.notificationGeneral' }),
        description: `Slack error: ${error?.error}${error.needed ? ` ${error.needed}` : ''}`,
        duration: 0,
      })
    })
  }

  const goToSignupStep = (platform: PlatformEnum, email?: string) => {
    if (process.env.REACT_APP_ENVIRONMENT === 'production') {
      posthog.startSessionRecording()
    }
    if (platform === PlatformEnum.Email) {
      localStorage.setItem('vtCreateUser', JSON.stringify({
        platform,
        mail: email,
        userEmail: email,
      }))
    }
    reduxDispatch(setAuthState(AuthStateEnum.createCompany))
    if (platform === PlatformEnum.Google || platform === PlatformEnum.Microsoft) {
      history.push(`${FrontendUrls.signup}${FrontendUrls.companyDetails}`)
    } else {
      history.push(`${FrontendUrls.signup}${FrontendUrls.personalInfo}`)
    }
  }

  const handleSignupWithMail = () => {
    if (process.env.REACT_APP_ENVIRONMENT === 'production') {
      posthog.startSessionRecording()
    }
    setSignupWithAnotherPlatformShowModal(false)
    localStorage.setItem('vtCreateUser', JSON.stringify({
      platform: 'email',
      mail: userEmail,
      userEmail: userEmail,
    }))
    localStorage.setItem('vtEmailInvites', JSON.stringify([]))
    reduxDispatch(setAuthState(AuthStateEnum.createCompany))
    history.push(`${FrontendUrls.signup}${FrontendUrls.personalInfo}`)
  }

  const handleUserData = (userData: ICheckUserId, platform: PlatformEnum, otherCreateUserData) => {
    setSelectedPlatform(platform)
    if (userData.type === 'USER_NOT_FOUND' && userData.subscriptionStatus === 'canceled') {
      notification.error({
        message: formatMessage({ id: 'connect.subscriptionExpiredTitle' }),
        description: formatMessage({ id: 'error.subscriptionExpired' }),
        duration: 0,
      })
      return
    }

    if (userData.type === 'USER_NOT_FOUND') {
      setContactAdminModal({
        platform,
        organizationName: userData.organizationName || '',
        contactEmail: userData.contactEmail || '',
        adminContacts: userData.adminContacts || [],
      })
      track('ORG_ALREADY_EXISTS_PAGE_VIEWED', {
        ...omit(userData || {}, ['adminContacts', 'slackUser']),
        platform,
      })
      setCompanyExists(true)
      setSigninInProgress(false)
      return
    }

    if (userData.type === 'COMPANY_NOT_FOUND') {
      localStorage.setItem('vtCreateUser', JSON.stringify({
        name: userData.name,
        id: `${platform}-${userData.id}`,
        mail: userData.mail,
        userEmail: userData.mail,
        platform,
        ...otherCreateUserData,
      }))

      if (userData.existsOnAnotherPlatform && userData.existingOrganizationName) {
        track('ORG_EXISTS_ON_ANOTHER_PLATFORM_OPEN_MODAL', { platform, existingOrganizationName: userData.existingOrganizationName })
        setExistsOnAnotherPlatform(userData.existsOnAnotherPlatform)
        setExistingOrganizationName(userData.existingOrganizationName)
        setOtherPlatformLogin(true)
        return
      }
    }
    setUserEmail(userData.mail || '')
    goToSignupStep(platform)
  }

  const handleSigninError = (error, platform) => {
    setSigninInProgress(false)
    let errorMessage

    if (typeof error === 'object' && !(error instanceof Error)) {
      if (error.error_description) {
        errorMessage = error.error_description
      } else if (error.error) {
        errorMessage = error.error
      } else {
        errorMessage = JSON.stringify(error, null, 2)
      }
    } else {
      if (typeof error === 'string') {
        errorMessage = error
      } else {
        errorMessage = error.toString()
      }
    }

    let platformText
    if (platform === 'microsoft') {
      track('CONNECT_WITH_MICROSOFT_ERROR', { errorMessage, platform })
      platformText = 'Microsoft Teams'
    } else if (platform === 'google') {
      track('CONNECT_WITH_GOOGLE_ERROR', { errorMessage, platform })
      platformText = 'Google'
    } else if (platform === 'slack') {
      track('CONNECT_WITH_SLACK_ERROR', { errorMessage, platform })
      platformText = 'Slack'
    }

    // This can turn out to be a switch statement if we encounter more error cases
    // that require different actions/descriptions
    if (errorMessage.includes('interaction_required') || errorMessage.includes('invalid_grant')) {
      notification.error({
        message: formatMessage({ id: 'connect.interactionRequiredMSErrorTitle' }),
        description: <div>
          <Paragraph>{ formatMessage({ id: 'connect.interactionRequiredMSErrorDescription1' }) }</Paragraph>
          <Paragraph>{ formatMessage({ id: 'connect.interactionRequiredMSErrorDescription2' }) }</Paragraph>
          <Paragraph keyboard copyable>{ errorMessage }</Paragraph>
        </div>,
        duration: 0,
        btn: <Button onClick={() => connectWithMicrosoft(true)}>{ formatMessage({ id: 'connect.login' }) }</Button>,
      })
    } else {
      notification.error({
        message: formatMessage({ id: 'connect.platformErrorTitle' }),
        description: <div>
          <Paragraph>{ formatMessage({ id: 'connect.platformErrorDescription1' }) }</Paragraph>
          <Paragraph keyboard copyable>{ errorMessage }</Paragraph>
          <Paragraph>{ formatMessage({ id: 'connect.platformErrorDescription2' }, { platform: platformText }) }</Paragraph>
        </div>,
        duration: 0,
      })
      // Do not track Slack "access_denied" in Sentry, as it's handled above
      if (!errorMessage.includes('access_denied')) {
        Sentry.captureException(error)
      }
    }
  }

  const authSigin = async (username: string, token: string, platform: string, botToken?: string) => {
    const signInResponse = await signin(username) as IAuthNextStep
    if (signInResponse.challengeName === 'CUSTOM_CHALLENGE' && signInResponse.challengeParam?.question === 'token') {
      const cognitoResponse = await confirmSigninWithCustomChallengeAnswer(token, { loginType: platform })
      localStorage.setItem('userId', cognitoResponse.username)

      if (platform === 'slack') {
        try {
          await updateSlackToken(token, botToken)
        } catch (error) {
          if(error?.response?.data?.error && (error?.response.data.error === 'error.botTokenNotExist' || error?.response.data.error === 'error.botTokenNotValid')) {
            setSigninInProgress(false)
            setShowSlackLoginRequiredModal(true)
            return
          }
        }
      }
      reduxDispatch(setUserId(cognitoResponse.username))
      await getCompanyAndUser(cognitoResponse.username)
      track('LOGIN_COMPLETED', {
        platform,
        timestamp: new Date().toDateString(),
        source: 'app',
        signupVariation: 'B_2025_06',
        status: 'completed',
        fromPage: 'singup', // For testig mix panel
      })
      reduxDispatch(setAuthState(AuthStateEnum.signedIn))
      if (redirectUrl) {
        history.push(redirectUrl)
      }
    }
  }

  const signupStartAnalytics = (platform: PlatformEnum) => {
    let utmCookieDecoded
    const utmCookie = getCookieByName('_vt_sc')
    if (utmCookie) {
      try {
        utmCookieDecoded = parseAndPrepareUtmCookie(utmCookie)
      } catch (error) {
        Sentry.captureException(error)
      }
    }
    track('SIGNUP_STARTED', {
      platform,
      timestamp: new Date().toDateString(),
      source: 'app',
      signupVariation: 'B_2025_06',
      status: 'started',
      utmCookie: utmCookieDecoded,
    })
  }

  const connectWithMail = async () => {
    const platform: PlatformEnum = PlatformEnum.Email
    setSigninInProgress(true)

    signupStartAnalytics(platform)
    const { email }: { email: string } = await form.validateFields()
    const domain = email.split('@')[1]
    
    // Dynamically import blacklist to reduce initial bundle size
    const { blacklistDomains } = await import('@vacationtracker/shared/data/blacklist-domain')
    if (blacklistDomains.includes(domain)) {
      notification.error({
        message: formatMessage({ id: 'error.emailError' }),
        description: formatMessage({ id: 'error.emailError.disposable' }),
      })
      setSigninInProgress(false)
      return
    }
    try {
      if (email) {
        setUserEmail(email)
        await getUsersByEmail(email)
      } else {
        throw new Error('EMAIL_REQUIRED')
      }
    } catch (error) {
      console.log('ERR', error.message, error.code, error.status, error.statusCode)
      setSigninInProgress(false)
      if (error?.response?.data?.code === EmailPlatformUserInactive.code) {
        notification.error({
          message: formatMessage({ id: 'error.signin.inactiveUser' }),
          key: 'USER_INACTIVE',
          description: formatMessage({ id: 'error.auth.companyExists.line1' }),
          duration: 0,
        })
        return
      }

      if (error.response?.data?.code === UserExistsException.code) {
        const { data } = error.response.data
        notification.error({
          message: formatMessage({ id: 'error.signup.alreadySignedUp.title' }),
          description: data.status.toUpperCase() === 'ACTIVE' ? (<>
            <Paragraph>
              {formatMessage(
                { id: 'error.signup.alreadySignedUp.descriptionLine1' },
                {
                  email: data.email,
                  text: (chunks: React.ReactNode) => <Text keyboard>{chunks}</Text>,
                }
              )}
            </Paragraph>
            <Paragraph>{formatMessage({ id: 'error.signup.alreadySignedUp.descriptionLine2' })}</Paragraph>
          </>) : (<>
            <Paragraph>
              {formatMessage(
                { id: 'error.signup.alreadySignedUpInactive.descriptionLine1' },
                {
                  email: data.email,
                  text: (chunks: React.ReactNode) => <Text keyboard>{chunks}</Text>,
                }
              )}
            </Paragraph>
            <Paragraph>{formatMessage({ id: 'error.signup.alreadySignedUpInactive.descriptionLine2' })}</Paragraph>
            <Paragraph code copyable>{ JSON.stringify({ cid: data.companyId, uid: data.id }, null, 4) }</Paragraph>
          </>),
          key: UserExistsException.code,
          btn: data.status.toUpperCase() === 'ACTIVE' ? (
            <Button onClick={() => {
              notification.destroy(UserExistsException.code)
              goToLogin()
            }}>{ formatMessage({ id: 'app.login' }) }</Button>
          ) : (
            <Button onClick={() => {
              notification.destroy(UserExistsException.code)
              openSupportChat()
            }}>{ formatMessage({ id: 'app.contactSupport' }) }</Button>
          ),
        })
        return
      }

      if (error.response?.data?.code === EmailNotValid.code) {
        notification.error({
          message: formatMessage({ id: 'error.emailError' }),
          key: 'NOT_VALID_EMAIL',
          description: <Text>{
            formatMessage({ id: 'form.validEmailRequired' })
          }</Text>,
        })
        return
      }

      if (error.message === 'ALREADY_SIGNED_UP') {
        notification.error({
          message: formatMessage({ id: 'error.signup.alreadySignedUp.title' }),
          description: <>
            <Text>
              {formatMessage(
                { id: 'error.signup.alreadySignedUp.descriptionLine1' },
                {
                  email,
                  text: (chunks: React.ReactNode) => <Text keyboard>{chunks}</Text>,
                }
              )}
            </Text>
            <Paragraph>{ formatMessage({ id: 'error.signup.alreadySignedUp.descriptionLine2' }) }</Paragraph>
          </>,
          key: 'ALREADY_SIGNED_UP',
          btn: (<>
            <Button onClick={() => {
              goToLogin()
              notification.destroy('ALREADY_SIGNED_UP')
            }}>{ formatMessage({ id: 'app.login' }) }</Button>
            <Button onClick={() => {
              goToLogin('forgotpassword')
              notification.destroy('ALREADY_SIGNED_UP')
            }}>{ formatMessage({ id: 'app.resetPassword' }) }</Button>
          </>),
          duration: 0,
        })
        return
      } else if (error.message.includes('404')) {
        const { data } = error.response.data
        if (error.response?.data?.code === UserNotFound.code && data.domainPlatform) {
          setDomainPlatform(data.domainPlatform)
          setSignupWithAnotherPlatformShowModal(true)
          return
        }
        goToSignupStep(PlatformEnum.Email, email as string)
      } else {
        Sentry.captureException(error)
      }
    }
  }

  const connectWithGoogle = () => {
    const platform: PlatformEnum = PlatformEnum.Google
    setSigninInProgress(true)
    track('LOGIN_PAGE_LOGIN_WITH_GOOGLE_BUTTON_CLICKED', { platform })
    signupStartAnalytics(platform)

    googleAuth.signin()
      .then(async () => {
        const googleUser = googleAuth.getSignedInUser()
        if (!googleUser.hd) {
          notification.error({
            message: formatMessage({ id: 'connect.google.notWorkspaceUserTitle' }),
            description: formatMessage({ id: 'connect.google.notWorkspaceUserDescription' }),
            duration: 0,
          })
          return
        }
        try {
          await authSigin(`google-${googleUser.sub}`, googleAuth.getAccessToken() , platform)
        } catch (error) {
          const userData: ICheckUserId = await getGoogleUserId(googleAuth.getIdToken())
          handleUserData(userData, platform, {
            googleDomain: googleUser.hd,
            imageUrl: googleUser.picture,
            googleUserId: googleUser.sub,
          })
        }
      })
      .catch(e => handleSigninError(e, 'google'))
  }

  const connectWithSlack = () => {
    const platform: PlatformEnum = PlatformEnum.Slack
    setSigninInProgress(true)
    track('CONNECT_WITH_SLACK_BUTTON_CLICKED', { platform })
    signupStartAnalytics(platform)

    slackAuth.connect()
      .then(async result => {
        const user = await slackAuth.getUser(result.authed_user.id, result.authed_user.access_token)
        dispatch(actions.setCreateUser({
          platform: PlatformEnum.Slack,
          name: user.real_name || user.name || '',
          mail: user?.profile?.email || user.email,
          slackUserId: result.authed_user.id,
          isOwner: Boolean(user.is_owner),
          isAdmin: Boolean(user.is_admin),
          imageUrl: user?.profile?.image_512 || '',
          id: result.authed_user.id,
        }))
        history.push(`${FrontendUrls.installSlackBot}/${result.team.id}?realName=${user.real_name || user.name || ''}`)
      })
      .catch(e => {
        console.log('SLACK SIGNIN ERROR', e)
        handleSigninError(e, 'slack')
      })
  }

  const connectWithMicrosoft = (interactionRequired = false) => {
    const platform: PlatformEnum = PlatformEnum.Microsoft
    setSigninInProgress(true)
    track('CONNECT_WITH_MICROSOFT_BUTTON_CLICKED', { platform })
    signupStartAnalytics(platform)

    // This requires promises, not async/await because window.open requires sync not async flow
    msAuth.signin(['user.readbasic.all', 'team.readbasic.all'], interactionRequired)
      .then(async ([tokens, msUser, tenantId]: [ITokens, IMicrosoftUser, TenantId]) => {
        try {
          const vacationTrackerUserId = await getVacationTrackerUserId(msUser.id, tenantId, tokens.accessToken)
          await authSigin(vacationTrackerUserId, tokens.accessToken, platform)
        } catch (error) {
          msAuth.setTokens(tokens)
          const userData: ICheckUserId = await getMsUserId(tenantId, tokens.accessToken)
          handleUserData(userData, platform, { tenantId, msUserId: userData.id })
        }
      })
      .catch(e => handleSigninError(e, 'microsoft'))
  }

  const getVacationTrackerUserId = async (microsoftId: string, tenantId: string, accessToken: string): Promise<string> => {
    const defaultUserId = `microsoft-${microsoftId}`
    try {
      const userData: ICheckUserId = await getMsUserId(tenantId, accessToken)
      return userData.id || defaultUserId // might be that migration was from another platform
    } catch (error) {
      return defaultUserId
    }
  }

  const connectWithPlatform = (platform: PlatformEnum) => {
    track('ORG_EXISTS_ON_ANOTHER_PLATFORM_CHOOSE_EXISTING', { platform })
    localStorage.removeItem('createUser')
    setOtherPlatformLogin(false)
    if (platform === PlatformEnum.Slack) {
      connectWithSlack()
    } else if (platform === PlatformEnum.Microsoft) {
      connectWithMicrosoft()
    } else if (platform === PlatformEnum.Google) {
      connectWithGoogle()
    }
  }

  return (
    <>
      <Title level={3}>{
        selectedPlan === SubscriptionPlanEnum.freemium
          ? <IntlMessages id="app.freeForever" />
          : <IntlMessages id='createCompany.freeTrialFullAccess' />
      }</Title>
      <Paragraph className="auth-sidebar-info-text">{
        selectedPlan === SubscriptionPlanEnum.freemium
          ? <IntlMessages id="createCompany.freemiumSignupSubtitle" />
          : <IntlMessages id='createCompany.FullAccessNoCommitmentNoCreditCard' />
      }</Paragraph>
      <Spin spinning={signinInProgress}>
        <div className="auth-sidebar-content">
          {(!focusedPlatform || focusedPlatform === PlatformEnum.Microsoft) &&
            <Button icon={<TeamsIcon />} block onClick={() => connectWithMicrosoft()}>
              <IntlMessages id="createCompany.connectWithMicrosoft" />
            </Button>
          }
          {(!focusedPlatform || focusedPlatform === PlatformEnum.Google) &&
            <Button icon={<GoogleIcon />} size='large' className="vt-auth" block onClick={() => connectWithGoogle()}>
              <IntlMessages id="connect.signUpWithGoogle" />
            </Button>
          }
          {(!focusedPlatform || focusedPlatform === PlatformEnum.Slack) &&
            <Button icon={<SlackIcon />}   block onClick={() => connectWithSlack()}>
              <IntlMessages id="createCompany.connectWithSlack" />
            </Button>
          }
          {!focusedPlatform &&
            <Divider>{ formatMessage({id: 'app.or'}).toLowerCase() }</Divider>
          }
          {(!focusedPlatform || focusedPlatform === PlatformEnum.Email) &&
            <>
              <Paragraph className="connect-with-email"><IntlMessages id="app.email" /></Paragraph>
              <Form
                name="connect-with-email"
                form={form}
                layout="horizontal"
                onFinish={connectWithMail}
              >
                <Form.Item
                  name="email"
                  rules={[{ required: true, type: 'email', message: (<IntlMessages id="connect.pleaseInputValidEmail" />) }]}
                >
                  <Input variant="outlined" style={{ borderRadius: '10px' }} placeholder={formatMessage({ id: 'createCompany.placeholderEmail' })} />
                </Form.Item>
                <Form.Item>
                  <Button block htmlType="submit" type="primary">
                    <IntlMessages id="createCompany.connectWithEmail" />
                  </Button>
                </Form.Item>
              </Form>
            </>
          }
        </div>
      </Spin>
      <Paragraph className="auth-sidebar-login">
        <IntlMessages id="connect.haveAnAccount" />&nbsp;
        <Link  onClick={() => goToLogin()}>
          <IntlMessages id="app.login" />
        </Link>
      </Paragraph>

      {companyExists && contactAdminModal &&
        <CompanyExistsModal
          companyExists={companyExists}
          setCompanyExists={setCompanyExists}
          contactAdminModal={contactAdminModal}
          userEmail={userEmail}
          userWorkspaceName={userWorkspaceName}
        />
      }

      {otherPlatformLogin && selectedPlatform &&
        <OtherPlatformLoginModal
          otherPlatformLogin={otherPlatformLogin}
          connectWithPlatform={connectWithPlatform}
          closeOtherPlatformLoginModal={() => {
            track('ORG_EXISTS_ON_ANOTHER_PLATFORM_CLOSE_MODAL', {})
            goToLogin()
          }}
          existingOrganizationName={existingOrganizationName}
          existsOnAnotherPlatform={existsOnAnotherPlatform}
          selectedPlatform={selectedPlatform}
        />
      }

      {showSlackLoginRequiredModal &&
        <Modal
          title={formatMessage({ id: 'error.slack.usersLoginRequiredTitle' })}
          open={showSlackLoginRequiredModal}
          onOk={getSlackTokens}
          cancelText={formatMessage({ id: 'app.cancel'})}
          okText={formatMessage({ id: 'connect.slack.usersLoginRequiredButton'})}
          onCancel={() => {
            dispatch(setUserId(localStorage.getItem('userId') as string))
            setShowSlackLoginRequiredModal(false)
          }}
        >
          <p><IntlMessages id="connect.slack.usersLoginRequiredDescription1" /></p>
        </Modal>
      }

      {companyAlreadySignedUpShowModal &&
        <CompanyAlreadySignedUpModal
          showModal={companyAlreadySignedUpShowModal}
          existsOnAnotherPlatform={existsOnAnotherPlatform}
          connectWithPlatform={connectWithPlatform}
          handleCloseCompanyAlreadySignedUpModal={() => {
            setCompanyAlreadySignedUpShowModal(false)
            setSigninInProgress(false)
          }}
        />
      }

      {signupWithAnotherPlatformShowModal && domainPlatform &&
        <SignupWithAnotherPlatformModal
          showModal={signupWithAnotherPlatformShowModal}
          domainPlatform={domainPlatform}
          connectWithPlatform={connectWithPlatform}
          handleSignupWithMail={handleSignupWithMail}
          handleCloseSignupWithAnotherPlatformModal={() => setSignupWithAnotherPlatformShowModal(false)}
        />
      }
    </>
  )
}

export default ConnectPlatform
